/* Саммери встреч: расшифровка разговора и рабочая выжимка из него.

   Три таба — три способа получить одно и то же:
     • Саммери — принести готовую запись файлом;
     • Запись — отправить бота в Zoom / Meet / Teams, он запишет звонок сам;
     • История — что уже сделано, с расшифровками и саммери.

   Работа идёт минутами и на сервере, поэтому экран только заводит карточку
   и уходит на неё: ход дела показывает уже она. */

import { clockLen, ddmm, meetingBusy, meetingTitle, MEETING_STAGE } from "@mera/shared";
import { Button, List, ListInput, ListItem, Segmented, SegmentedButton } from "konsta/react";
import React, { useRef, useState } from "react";

import { Ion, IconTile } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelList } from "../components/skeleton.jsx";
import { Bar, Empty, Hint } from "../components/ui.jsx";
import { api, uploadWithProgress } from "../lib/api.js";
import { useNavigate } from "../lib/router.jsx";
import { useFetch } from "../lib/store.jsx";
import { alertDialog, haptic } from "../lib/tg.js";

const ITEM_COLORS = {
  primaryTextIos: "text-[color:var(--tg-text)]",
  secondaryTextIos: "text-[color:var(--tg-hint)]",
};

const TILE = { file: "#5856d6", call: "#30b0c7", done: "#34c759", work: "#ff9500", fail: "#8e8e93" };

const TABS = [
  { id: "summary", label: "Саммери" },
  { id: "record", label: "Запись" },
  { id: "history", label: "История" },
];

const size = (n) =>
  !n ? null : n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(n / 1024))} КБ`;

/* Плитка строки истории: цвет и значок говорят, на каком этапе встреча */
const rowTile = (m) =>
  m.status === "done"
    ? { icon: Ion.checkmarkDone, bg: TILE.done }
    : m.status === "error"
      ? { icon: Ion.warning, bg: TILE.fail }
      : m.status === "recording"
        ? { icon: Ion.videocam, bg: TILE.call }
        : { icon: Ion.hourglass, bg: TILE.work };

/** Что стоит справа: готовая встреча — когда и сколько шла, остальные — стадия */
const rowNote = (m) =>
  m.status === "done"
    ? [ddmm(m.createdAt), clockLen(m.durationSec)].filter(Boolean).join(" · ")
    : m.status === "error"
      ? "не вышло"
      : m.stage || MEETING_STAGE[m.status];

/* ── Таб «Саммери»: запись файлом ──────────────────────── */

function ByFile({ status, onStarted }) {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [instruction, setInstruction] = useState("");
  const [sent, setSent] = useState(0);
  const [busy, setBusy] = useState(false);

  const pick = (e) => {
    const chosen = e.target.files?.[0];
    e.target.value = "";
    if (!chosen) return;
    if (status?.maxBytes && chosen.size > status.maxBytes) {
      haptic.error();
      alertDialog(`Запись больше ${size(status.maxBytes)} — столько не принимаем.`);
      return;
    }
    haptic.light();
    setFile(chosen);
  };

  const start = async () => {
    if (!file || busy) return;
    haptic.medium();
    setBusy(true);
    setSent(0);
    try {
      const created = await uploadWithProgress("/meetings", file, {
        fields: { title, instruction },
        onProgress: setSent,
      });
      haptic.success();
      onStarted(created);
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="audio/*,video/*,.mp3,.m4a,.wav,.ogg,.opus,.mp4,.mov,.webm"
        className="hidden"
        onChange={pick}
      />

      <List strong inset>
        <ListItem
          link
          chevron={!busy}
          colors={ITEM_COLORS}
          media={<IconTile icon={Ion.headsetSolid} bg={TILE.file} />}
          title={file ? file.name : "Выбрать запись"}
          after={file ? size(file.size) : null}
          onClick={busy ? undefined : () => input.current?.click()}
        />
      </List>

      <List strong inset>
        <ListInput
          type="text"
          placeholder="Название встречи"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <ListInput
          type="textarea"
          inputClassName="!h-24 resize-none"
          placeholder="Что учесть в саммери: о чём встреча, кто участвовал, на что обратить внимание"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
        />
      </List>

      {busy ? (
        <div className="px-4 pt-4">
          <Bar value={Math.round(sent * 100)} />
          <div className="pt-2 flex items-center justify-between gap-3">
            <Hint>Отправляю запись</Hint>
            <span className="num text-[13px] font-semibold">{Math.round(sent * 100)}%</span>
          </div>
        </div>
      ) : (
        <div className="px-4 pt-4">
          <Button large rounded onClick={start} disabled={!file || !status?.stt}>
            Сделать саммери
          </Button>
        </div>
      )}

      <div className="px-4 pt-4">
        <Hint>
          Аудио или видео со встречи — до {size(status?.maxBytes) || "200 МБ"}. Без названия встречу
          назовёт саммери — по тому, о чём говорили. Расшифровка идёт несколько минут: закрывать
          приложение можно, готовое придёт в чат с ботом.
        </Hint>
      </div>
    </>
  );
}

/* ── Таб «Запись»: бот идёт на созвон ──────────────────── */

function ByCall({ status, onStarted }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!url.trim() || busy) return;
    haptic.medium();
    setBusy(true);
    try {
      const created = await api.post("/meetings/record", { url: url.trim(), title, instruction });
      haptic.success();
      onStarted(created);
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <List strong inset>
        <ListInput
          type="url"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="Ссылка на Zoom, Google Meet или Teams"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </List>

      <List strong inset>
        <ListInput
          type="text"
          placeholder="Название встречи"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <ListInput
          type="textarea"
          inputClassName="!h-24 resize-none"
          placeholder="Что учесть в саммери: о чём встреча, кто участвовал, на что обратить внимание"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
        />
      </List>

      <div className="px-4 pt-4">
        <Button large rounded onClick={start} disabled={busy || !url.trim() || !status?.recall}>
          {busy ? "Отправляю бота…" : "Отправить бота на встречу"}
        </Button>
      </div>

      <div className="px-4 pt-4">
        <Hint>
          Бот зайдёт в звонок под именем «{status?.botName || "Мера Деск"}» — если встреча
          с комнатой ожидания, его надо впустить. Запись начнётся сама, а после звонка придут
          расшифровка и саммери. Ссылку можно просто прислать боту в Telegram — подключится так же.
        </Hint>
      </div>
    </>
  );
}

/* ── Таб «История» ─────────────────────────────────────── */

function History({ items, loading, onOpen }) {
  if (loading && !items) return <SkelList rows={4} />;
  if (!items?.length) {
    return <Empty text="Встреч пока нет — сделайте саммери или отправьте бота на созвон" icon={Ion.headset} />;
  }

  return (
    <List strong inset>
      {items.map((m) => {
        const tile = rowTile(m);
        return (
          <ListItem
            key={m.id}
            link
            colors={ITEM_COLORS}
            media={<IconTile icon={tile.icon} bg={tile.bg} />}
            title={<span className="truncate">{meetingTitle(m)}</span>}
            after={<span className="whitespace-nowrap">{rowNote(m)}</span>}
            onClick={() => onOpen(m.id)}
          />
        );
      })}
    </List>
  );
}

/* ── Экран ─────────────────────────────────────────────── */

export default function Meetings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("summary");
  const { data: status } = useFetch("/meetings/status");
  const { data: items, loading, refetch } = useFetch("/meetings");

  /* Пока хоть одна встреча в работе, история подтягивается сама:
     человек видит, как «Расшифровываю» сменяется на готовое саммери */
  const working = (items || []).some(meetingBusy);
  React.useEffect(() => {
    if (tab !== "history" || !working) return undefined;
    const timer = setInterval(refetch, 5000);
    return () => clearInterval(timer);
  }, [tab, working, refetch]);

  const started = (m) => {
    refetch();
    navigate(`/meeting/${m.id}`);
  };

  const missing = [
    status && !status.stt ? "расшифровка (SONIOX_API_KEY)" : null,
    status && !status.llm ? "саммери (LLM_API_KEY)" : null,
    status && tab === "record" && !status.recall ? "бот на созвонах (RECALL_API)" : null,
  ].filter(Boolean);

  return (
    <Screen back title="Саммери встреч" bottomPad={40}>
      <div className="px-4">
        <Segmented strong rounded>
          {TABS.map((t) => (
            <SegmentedButton
              key={t.id}
              active={tab === t.id}
              onClick={() => {
                haptic.select();
                setTab(t.id);
              }}
            >
              <span className="whitespace-nowrap">{t.label}</span>
            </SegmentedButton>
          ))}
        </Segmented>
      </div>

      {missing.length ? (
        <div className="px-4 pt-3">
          <Hint>Не настроено: {missing.join(", ")}.</Hint>
        </div>
      ) : null}

      {tab === "summary" ? <ByFile status={status} onStarted={started} /> : null}
      {tab === "record" ? <ByCall status={status} onStarted={started} /> : null}
      {tab === "history" ? (
        <History items={items} loading={loading} onOpen={(id) => navigate(`/meeting/${id}`)} />
      ) : null}
    </Screen>
  );
}
